import React, { useEffect, useRef, useState } from "react";
import "../../../styles/settings.css";
import VerifiedNavigation from "../../navigation/components/VerifiedNavigation";
import { AuthServices } from "../../auth/services/AuthServices";
import { SettingsServices } from "../services/SettingsServices";
import { FaEdit } from "react-icons/fa";
import AddressAutocomplete from "../../../utils/components/AddressAutocomplete";

const Settings = () => {
  const [profile, setProfile] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [previewImages, setPreviewImages] = useState({});
  const [message, setMessage] = useState(null);

  const fetchProfile = async () => {
    try {
      const response = await AuthServices.fetchProfile();
      setProfile(response.data?.user);
      setPreviewImages(response.data?.user?.iconPhoto);
    } catch (error) {
      console.error("Error fetching profile info:", error);
    }
  };

  const handleEditClick = (field) => {
    setEditingField(field);
    setTempValue(profile[field] || "");
  };

  const handleSaveField = async (field) => {
    if (!tempValue) return;
    setLoading(true);

    try {
      await SettingsServices.updateProfile({ [field]: tempValue });
      setProfile((prev) => ({ ...prev, [field]: tempValue }));
      setEditingField(null);
      setLoading(false);
    } catch (error) {
      console.error("Error updating field:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageEditClick = (field) => {
    setCurrentImageField(field);
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentImageField) return;

    const previewURL = URL.createObjectURL(file);
    setPreviewImages((prev) => ({
      ...prev,
      [currentImageField]: previewURL,
    }));

    setLoading(true);
    const formData = new FormData();
    formData.append("images", file);

    try {
      const response = await SettingsServices.updateStoreLogo(formData);
      if (response.success) {
        setProfile((prev) => ({
          ...prev,
          [currentImageField]: response.data?.iconPhoto,
        }));
        setMessage("You may need to refresh the page to see the changes.");
      } else {
        console.error(response.message || response.data?.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <div className="settings">
      <VerifiedNavigation />
      <div className="settings-container">
        <div className="profile-container">
          <div className="profile-header">
            <h1>Business Profile</h1>
          </div>

          <div className="profile-details">
            {["businessName", "businessEmail", "businessNumber"].map(
              (field) => (
                <div className="profile-detail" key={field}>
                  <label>
                    {field === "businessName"
                      ? "Business Name:"
                      : field === "businessEmail"
                      ? "Business Email:"
                      : "Business Number:"}
                  </label>

                  {editingField === field ? (
                    <span>
                      <input
                        type="text"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleSaveField(field)
                        }
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveField(field)}
                        disabled={loading}
                      >
                        Save
                      </button>
                    </span>
                  ) : (
                    <span>
                      {profile?.[field] || "N/A"}{" "}
                      <FaEdit
                        className="edit-icon"
                        onClick={() => handleEditClick(field)}
                      />
                    </span>
                  )}
                </div>
              )
            )}

            <div className="profile-detail">
              <label>Business Address:</label>
              {editingField === "businessAddress" ? (
                <span>
                  <AddressAutocomplete
                    initialValue={profile.businessAddress?.formatted || ""}
                    onSelect={(tempValue) => setTempValue(tempValue)}
                  />
                  <button
                    onClick={() => handleSaveField("businessAddress")}
                    disabled={loading}
                  >
                    Save
                  </button>
                </span>
              ) : (
                <span>
                  {profile?.businessAddress?.formatted ||
                    "Please add your pickup location!"}
                  <FaEdit
                    className="edit-icon"
                    onClick={() => handleEditClick("businessAddress")}
                  />
                </span>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={handleFileChange}
            />

            <div className="profile-images-section">
              <div className="image-group">
                <label>Store Front Image</label>
                <div
                  className="image-wrapper clickable-image"
                  onClick={() => handleImageEditClick("storeFrontImage")}
                >
                  <img
                    src={
                      previewImages ||
                      profile?.iconPhoto ||
                      "/placeholder-storefront.png"
                    }
                    alt="Store Front"
                    className="business-image"
                  />
                  <div className="image-overlay">
                    <FaEdit className="overlay-icon" />
                    <span className="overlay-text">Change Image</span>
                  </div>
                </div>
                <br />
                {message && <div className="error-message">{message}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
